import { useEffect, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { PAYMENTS_ENABLED } from '@redevicer/shared'
import SellerVerificationSection from './SellerVerificationSection'
import { paymentsAPI, stripeConnectAPI } from '../api/client'

type Props = {
  open: boolean
  token: string
  defaultFullName?: string
  initialStep?: number
  onClose: () => void
  onProgress?: () => void
}

export default function SellerOnboardingWizard({
  open,
  token,
  defaultFullName = '',
  initialStep = 0,
  onClose,
  onProgress,
}: Props) {
  const { t } = useTranslation()
  const [step, setStep] = useState(initialStep)
  const [connectLoading, setConnectLoading] = useState(true)
  const [connectBusy, setConnectBusy] = useState(false)
  const [chargesEnabled, setChargesEnabled] = useState(false)
  const [exchangePassActive, setExchangePassActive] = useState<boolean | null>(null)
  const [exchangePassBusy, setExchangePassBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setStep(initialStep)
  }, [open, initialStep])

  useEffect(() => {
    if (!open || !token || !PAYMENTS_ENABLED) return
    setConnectLoading(true)
    stripeConnectAPI
      .getStatus(token)
      .then((res) => setChargesEnabled(!!res.data?.charges_enabled))
      .catch(() => setChargesEnabled(false))
      .finally(() => setConnectLoading(false))
    paymentsAPI
      .exchangePassStatus(token)
      .then((res) => setExchangePassActive(!!res.data?.active))
      .catch(() => setExchangePassActive(false))
  }, [open, token])

  if (!open) return null

  function startStripe() {
    setConnectBusy(true)
    stripeConnectAPI
      .createOnboardingLink(token)
      .then((res) => {
        const url = res.data?.url as string | undefined
        if (url) window.location.href = url
      })
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setConnectBusy(false))
  }

  function buyPass() {
    setExchangePassBusy(true)
    paymentsAPI
      .exchangePassCheckout(token)
      .then((res) => {
        if (res.data?.already_active) {
          setExchangePassActive(true)
          onProgress?.()
          return
        }
        const url = res.data?.checkout_url as string | undefined
        if (url) window.location.href = url
      })
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setExchangePassBusy(false))
  }

  const steps = PAYMENTS_ENABLED
    ? [t('account.wizardStepVerify'), t('account.wizardStepStripe'), t('account.wizardStepPass')]
    : [t('account.wizardStepVerify')]

  return (
    <div
      className="vc-wizard-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="seller-wizard-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="vc-wizard-panel">
        <header className="vc-wizard-header">
          <h2 id="seller-wizard-title">{t('account.wizardTitle')}</h2>
          <button type="button" className="vc-wizard-close" onClick={onClose}>
            {t('common.close')}
          </button>
        </header>

        <div className="vc-wizard-stepper" role="tablist" aria-label={t('account.wizardTitle')}>
          <div className="vc-wizard-stepper__rail" aria-hidden="true">
            <div
              className="vc-wizard-stepper__fill"
              style={{ width: `${(step / Math.max(steps.length - 1, 1)) * 100}%` }}
            />
          </div>
          <div className="vc-wizard-stepper__steps">
            {steps.map((label, i) => (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={step === i}
                className={`vc-wizard-stepper__step${step === i ? ' is-active' : ''}${i < step ? ' is-done' : ''}`}
                style={{ '--step-i': i } as CSSProperties}
                onClick={() => setStep(i)}
              >
                <span className="vc-wizard-stepper__dot" aria-hidden="true">
                  {i < step ? '✓' : i + 1}
                </span>
                <span className="vc-wizard-stepper__label">{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div key={step} className="vc-wizard-body">
          {step === 0 ? (
            <div className="vc-wizard-step-card">
              <SellerVerificationSection token={token} defaultFullName={defaultFullName} />
            </div>
          ) : null}
          {PAYMENTS_ENABLED && step === 1 ? (
            <div className="vc-wizard-step-card">
              {connectLoading ? <p className="meta-text">{t('common.loading')}</p> : null}
              {!connectLoading && chargesEnabled ? (
                <p className="vc-wizard-success">{t('vc.connect.ready')}</p>
              ) : null}
              {!connectLoading && !chargesEnabled ? (
                <>
                  <p className="vc-wizard-lead">{t('vc.connect.blurb')}</p>
                  <button type="button" className="vc-connect-btn" disabled={connectBusy} onClick={startStripe}>
                    {connectBusy ? '…' : t('vc.connect.cta')}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
          {PAYMENTS_ENABLED && step === 2 ? (
            <div className="vc-wizard-step-card">
              {exchangePassActive == null ? <p className="meta-text">{t('common.loading')}</p> : null}
              {exchangePassActive === true ? (
                <p className="vc-wizard-success">{t('exchange.passActive')}</p>
              ) : null}
              {exchangePassActive === false ? (
                <>
                  <p className="vc-wizard-lead">{t('exchange.passBlurb')}</p>
                  <button type="button" className="vc-connect-btn" disabled={exchangePassBusy} onClick={buyPass}>
                    {exchangePassBusy ? '…' : t('exchange.passCheckout')}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
